/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateTerminalResetTable,
  updateBinaryCaptureActive,
  updateBinaryCaptureReady,
  updateStopSocketConnection,
} from "../../../redux/monitoring/monitoringSlice";
import { useGetBankQuery } from "../../../redux/banks/bankAPI";
import { floorIndexOrZero, readDoorFlags } from "../../../utils/bankFloorOpenings";
// COMPONENTS
import TerminalFilters from "./TerminalFilters";
import TerminalTable from "./TerminalTable";
import TerminalJsonViewer from "./TerminalJsonViewer";
import MonitoringElevatorsFullView from "../fullView/MonitoringElevatorsFullView";
import MonitoringEscalatorsFullView from "../fullView/MonitoringEscalatorsFullView";
import MonitoringMovingWalkFullView from "../fullView/MonitoringMovingWalkFullView";
import MonitoringHeartBeat from "../monitoring/MonitoringHeartBeat";
import TerminalBankFloors from "./TerminalBankFloors";
import TerminalBankCommands from "./TerminalBankCommands";
import TerminalEndpointTable from "./TerminalEndpointTable";
import TerminalCommandPanel from "./TerminalCommandPanel";
import { EndpointMessagesProvider } from "./EndpointMessagesContext";
// INTERFACES
import { RootState } from "../../../app/store";
import {
  LayoutInterface,
  TerminalGlobalObject,
  TerminalFilterType,
} from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { encodeReplayFile, ReplayRecord } from "../../../utils/binaryReplayCodec";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { uuidToString } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import formatUuidT from "../../../flatbuffers/display/uuid";
import { toLiftType } from "../../../flatbuffers/api/requestSerializers";
// FLATBUFFER ENUMS — for human-readable JSON download
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { DeviceUsageContent } from "../../../flatbuffers/generated/usage/device-usage-content";
import { UsageVariant } from "../../../flatbuffers/generated/usage/usage-variant";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { DeviceStatusContent } from "../../../flatbuffers/generated/status/device-status-content";
import { StatusVariant } from "../../../flatbuffers/generated/status/status-variant";
import { Status } from "../../../flatbuffers/generated/status/status";
import { DeviceTrafficMessage } from "../../../flatbuffers/generated/traffic/device-traffic-message";
import { TrafficVariant } from "../../../flatbuffers/generated/traffic/traffic-variant";
import { Traffic } from "../../../flatbuffers/generated/traffic/traffic";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { ServiceVariant } from "../../../flatbuffers/generated/service/service-variant";
import { Service } from "../../../flatbuffers/generated/service/service";
import { DeviceControlMessage } from "../../../flatbuffers/generated/control/device-control-message";
import { ControlVariant } from "../../../flatbuffers/generated/control/control-variant";
import { Control } from "../../../flatbuffers/generated/control/control";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { FaultVariant } from "../../../flatbuffers/generated/fault/fault-variant";
import { Fault } from "../../../flatbuffers/generated/fault/fault";
import { DeviceGroupSupervisoryStatusContent } from "../../../flatbuffers/generated/group-supervisory-status/device-group-supervisory-status-content";
import { GroupSupervisoryStatusVariant } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status-variant";
import { GroupSupervisoryStatus } from "../../../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { DeviceGroupControlContent } from "../../../flatbuffers/generated/group-control/device-group-control-content";
import { GroupControlVariant } from "../../../flatbuffers/generated/group-control/group-control-variant";
import { GroupControl } from "../../../flatbuffers/generated/group-control/group-control";
import { DeviceControllerInfoContent } from "../../../flatbuffers/generated/controller-info/device-controller-info-content";
import { ControllerInfoVariant } from "../../../flatbuffers/generated/controller-info/controller-info-variant";
import { ControllerInfoContent } from "../../../flatbuffers/generated/controller-info/controller-info-content";

function TerminalPage({ layouts }: { layouts: LayoutInterface[] }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [terminalBanks, setTerminalBanks] = useState<TerminalGlobalObject[]>([]);
  const [terminalLifts, setTerminalLifts] = useState<TerminalGlobalObject[]>([]);
  const [liftNameMap, setLiftNameMap] = useState<Record<string, string>>({});
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
  const [bankId, setBankId] = useState<string>("");
  const [liftId, setLiftId] = useState<string>("");
  const [serverId, setServerId] = useState<string>("");
  const [channelId, setChannelId] = useState<string>("");
  const [bankLiftId, setBankLiftId] = useState<string>("");
  const [socketMessages, setSocketMessages] = useState<SocketMessageInterface[]>([]);
  const [filterType, setFilterType] = useState<TerminalFilterType>("ALL");
  const [token, setToken] = useState<string | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    showTerminalJsonViewer,
    showTerminalTable,
    terminalShowBankFloors,
    terminalMode,
    showTerminalCommandPanel,
  } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data } = useGetBankQuery(
    { token, bankId: bankId || bankLiftId },
    { skip: !token, refetchOnMountOrArgChange: false },
  );

  // ************************************************ */
  // REFS ****************************************** */
  const capturedMessagesRef = useRef<ReplayRecord[]>([]);
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const refreshInFlightRef = useRef(false);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickStartCapture = () => {
    capturedMessagesRef.current = [];
    dispatch(updateBinaryCaptureActive(true));
    dispatch(updateBinaryCaptureReady(false));
  };

  const onClickStopCapture = () => {
    dispatch(updateBinaryCaptureActive(false));
    dispatch(updateBinaryCaptureReady(true));
  };

  const onClickDownloadCapture = () => {
    const blob = encodeReplayFile(capturedMessagesRef.current);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `capture_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.ln2r`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const onClickRefreshConnection = () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    refreshTimersRef.current.forEach(clearTimeout);
    refreshTimersRef.current = [];
    dispatch(updateTerminalResetTable(true));
    const t1 = setTimeout(() => dispatch(updateTerminalResetTable(false)), 100);
    dispatch(updateStopSocketConnection(true));
    const t2 = setTimeout(() => {
      dispatch(updateStopSocketConnection(false));
      refreshInFlightRef.current = false;
    }, 500);
    refreshTimersRef.current = [t1, t2];
  };

  const onClickDownloadJsonFile = () => {
    try {
      const ek = (obj: any, value: number): string => obj[value] ?? String(value);

      const resolveServiceMode = (sm: SocketMessageInterface["serviceMode"]) => {
        if (!sm) return null;
        const deviceTypeName = ek(DeviceServiceContent, sm.messageType);
        const devMsg = sm.message as any;
        return {
          messageType: deviceTypeName,
          message:
            devMsg == null
              ? null
              : {
                  ...devMsg,
                  messageType: ek(ServiceVariant, devMsg.messageType),
                  message:
                    devMsg.message == null
                      ? null
                      : { ...devMsg.message, message: ek(Service, devMsg.message.message) },
                },
        };
      };

      const resolveFaults = (faults: SocketMessageInterface["activeFaults"]) =>
        faults.map((f) => {
          const deviceTypeName = ek(FaultContent, f.faultType);
          const devMsg = f.fault as any;
          return {
            faultType: deviceTypeName,
            fault:
              devMsg == null
                ? null
                : {
                    ...devMsg,
                    faultType: ek(FaultVariant, devMsg.faultType),
                    fault:
                      devMsg.fault == null
                        ? null
                        : { ...devMsg.fault, message: ek(Fault, devMsg.fault.message) },
                  },
          };
        });

      const resolveContentMessage = (rawMsgType: number, raw: any): any => {
        if (raw == null) return raw;
        switch (rawMsgType) {
          case MessageContent.Usage_UsageMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceUsageContent, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      usageType: ek(UsageVariant, devMsg.usageType),
                      usage:
                        devMsg.usage == null
                          ? null
                          : { ...devMsg.usage, message: ek(Usage, devMsg.usage.message) },
                    },
            };
          }
          case MessageContent.Status_StatusMessage: {
            const devMsg = raw.status;
            return {
              ...raw,
              statusType: ek(DeviceStatusContent, raw.statusType),
              status:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      statusType: ek(StatusVariant, devMsg.statusType),
                      status:
                        devMsg.status == null
                          ? null
                          : { ...devMsg.status, message: ek(Status, devMsg.status.message) },
                    },
            };
          }
          case MessageContent.Traffic_TrafficMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceTrafficMessage, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      trafficType: ek(TrafficVariant, devMsg.trafficType),
                      traffic:
                        devMsg.traffic == null
                          ? null
                          : { ...devMsg.traffic, message: ek(Traffic, devMsg.traffic.message) },
                    },
            };
          }
          case MessageContent.Service_ServiceMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceServiceContent, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      messageType: ek(ServiceVariant, devMsg.messageType),
                      message:
                        devMsg.message == null
                          ? null
                          : { ...devMsg.message, message: ek(Service, devMsg.message.message) },
                    },
            };
          }
          case MessageContent.Control_ControlMessage: {
            const devMsg = raw.control;
            return {
              ...raw,
              controlType: ek(DeviceControlMessage, raw.controlType),
              control:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      controlType: ek(ControlVariant, devMsg.controlType),
                      control:
                        devMsg.control == null
                          ? null
                          : { ...devMsg.control, message: ek(Control, devMsg.control.message) },
                    },
            };
          }
          case MessageContent.Fault_FaultMessage: {
            const devMsg = raw.fault;
            return {
              ...raw,
              faultType: ek(FaultContent, raw.faultType),
              fault:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      faultType: ek(FaultVariant, devMsg.faultType),
                      fault:
                        devMsg.fault == null
                          ? null
                          : { ...devMsg.fault, message: ek(Fault, devMsg.fault.message) },
                    },
            };
          }
          case MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceGroupSupervisoryStatusContent, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      statusType: ek(GroupSupervisoryStatusVariant, devMsg.statusType),
                      status:
                        devMsg.status == null
                          ? null
                          : {
                              ...devMsg.status,
                              message: ek(GroupSupervisoryStatus, devMsg.status.message),
                            },
                    },
            };
          }
          case MessageContent.GroupControl_GroupControlMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceGroupControlContent, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      controlType: ek(GroupControlVariant, devMsg.controlType),
                      control:
                        devMsg.control == null
                          ? null
                          : {
                              ...devMsg.control,
                              message: ek(GroupControl, devMsg.control.message),
                            },
                    },
            };
          }
          case MessageContent.ControllerInfo_ControllerInfoMessage: {
            const devMsg = raw.message;
            return {
              ...raw,
              messageType: ek(DeviceControllerInfoContent, raw.messageType),
              message:
                devMsg == null
                  ? null
                  : {
                      ...devMsg,
                      infoType: ek(ControllerInfoVariant, devMsg.infoType),
                      info:
                        devMsg.info == null
                          ? null
                          : {
                              ...devMsg.info,
                              messageType: ek(ControllerInfoContent, devMsg.info.messageType),
                            },
                    },
            };
          }
          default:
            return raw;
        }
      };

      const serializable = socketMessages.map((socketMsg) => {
        const {
          bankId: bid,
          liftId: lid,
          direction,
          doorZone,
          escalatorControl,
          activeFaults,
          serviceMode,
          msg: rawMsg,
          ...rest
        } = socketMsg;

        const rawOneHotMap = (rest as any).oneHotMap;
        const resolvedOneHotMap =
          rawOneHotMap != null
            ? Object.fromEntries(
                Object.entries(rawOneHotMap as Record<string, any>).map(([k, v]) => [
                  k,
                  { ...v, messageType: ek(MessageContent, (v as any).messageType) },
                ]),
              )
            : rawOneHotMap;

        return {
          ...rest,
          ...(rawOneHotMap != null ? { oneHotMap: resolvedOneHotMap } : {}),
          bankId: uuidToString(bid),
          liftId: uuidToString(lid),
          direction: direction != null ? ek(Usage, direction) : null,
          doorZone: doorZone != null ? ek(Usage, doorZone) : null,
          escalatorControl: escalatorControl != null ? ek(Control, escalatorControl) : null,
          activeFaults: resolveFaults(activeFaults),
          serviceMode: resolveServiceMode(serviceMode),
          msg: {
            ...rawMsg,
            contentType: ek(EncodedMessageContent, rawMsg.contentType),
            messageType: ek(MessageContent, rawMsg.messageType),
            content: rawMsg.content
              ? {
                  ...(rawMsg.content as any),
                  messageType: ek(MessageContent, (rawMsg.content as any).messageType),
                  message: resolveContentMessage(
                    (rawMsg.content as any).messageType,
                    (rawMsg.content as any).message,
                  ),
                }
              : null,
          },
        };
      });
      const jsonString = JSON.stringify(serializable, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "replayFile";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(updateTerminalResetTable(false));
    };
  }, []);

  useEffect(() => {
    return () => {
      refreshTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    const newBanks: TerminalGlobalObject[] = [];
    const newLifts: TerminalGlobalObject[] = [];
    const nameMap: Record<string, string> = {};
    layouts.forEach((layout: LayoutInterface) => {
      newBanks.push({
        id: layout.idBank,
        name: layout.name,
        channelId: layout.channelId,
        serverId: layout.idServer,
        bankId: layout.idBank,
        liftId: "",
      });
      if (layout.lifts) {
        layout.lifts.forEach((lift: LiftInterface) => {
          newLifts.push({
            id: String(lift.id),
            name: `${layout.name} - ${String(lift.name ?? "")}`,
            channelId: layout.channelId,
            serverId: layout.idServer,
            bankId: layout.idBank,
            liftId: String(lift.id),
          });
          nameMap[String(lift.id)] = String(lift.name ?? "");
        });
      }
    });
    setTerminalBanks(newBanks);
    setTerminalLifts(newLifts);
    setLiftNameMap(nameMap);
  }, [layouts]);

  useEffect(() => {
    if (data && data.data) {
      const b = data.data;
      // Normalize UuidT → string and snake_case → camelCase. Was written to
      // mirror MonitoringBank.tsx's own normalizeFloors/normalizeLiftFloors
      // — both since migrated onto the same shared readers this file uses
      // (which also fixed a pre-existing, standalone bug tracked separately
      // at the time: MonitoringBank.tsx's per-lift reader used to default
      // front to false, where every sibling consumer of this same data —
      // this file included — defaults it to true).
      // FlatBuffer unpack returns UuidT for UUID fields; String(UuidT) → "[object Object]",
      // breaking the BankMessagesContext key lookup in FloorCellIcon (no icons rendered).
      const normalizeFloors = (floors: any[]) =>
        floors.map((f: any) => {
          const doors = readDoorFlags(f, true, false);

          return {
            ...f,
            id: formatUuidT(f.id) || f.id,
            bankId: formatUuidT(f.bankId ?? f.bank_id) || f.bankId || f.bank_id,
            floorNo: f.floorNo ?? f.floor_no ?? 0,
            floorIndex: floorIndexOrZero(f),
            floorBelowLobby: f.floorBelowLobby ?? f.floor_below_lobby ?? false,
            frontDoor: doors.front,
            rearDoor: doors.rear,
          };
        });
      const rawApiBankFloors: any[] = b.bankFloors ?? b.bank_floors ?? [];
      const resolvedFloors = normalizeFloors(rawApiBankFloors);
      const normalizedLifts = (b.lifts ?? []).map((l: any) => ({
        ...l,
        id: formatUuidT(l.id) || l.id,
        bankId: formatUuidT(l.bankId ?? l.bank_id) || l.bankId || l.bank_id,
      }));
      const bankIdStr = formatUuidT(b.id) || b.id;
      setBank({
        ...b,
        id: bankIdStr,
        type: toLiftType(b.type),
        lifts: normalizedLifts,
        bankFloors: resolvedFloors,
      });
      // Merge lift names using the already-normalized string IDs
      if (normalizedLifts.length > 0) {
        setLiftNameMap((prev) => {
          const updated = { ...prev };
          let changed = false;
          normalizedLifts.forEach((lift: any) => {
            const liftIdStr = String(lift.id ?? "");
            const liftNameStr = String(lift.name ?? "");
            if (liftIdStr && liftNameStr && !updated[liftIdStr]) {
              updated[liftIdStr] = liftNameStr;
              changed = true;
            }
          });
          return changed ? updated : prev;
        });
      }
    }
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className={`terminal__drawer__container__${theme}`}>
      <TerminalFilters
        terminalBanks={terminalBanks}
        terminalLifts={terminalLifts}
        setBankId={setBankId}
        bankId={bankId}
        setLiftId={setLiftId}
        setChannelId={setChannelId}
        setServerId={setServerId}
        setBankLiftId={setBankLiftId}
        liftId={liftId}
        filterType={filterType}
        setFilterType={setFilterType}
        onClickDownloadJsonFile={onClickDownloadJsonFile}
        onClickStartCapture={onClickStartCapture}
        onClickStopCapture={onClickStopCapture}
        onClickDownloadCapture={onClickDownloadCapture}
        onClickRefreshConnection={onClickRefreshConnection}
      />
      <br />
      {/** ************************************************** */}
      {/** COMMAND PANEL — always available for the selected bank, so the
       * operator can fire an action and watch its TX row land in the table
       * below without depending on the bank's adapter protocol. */}
      {terminalMode !== "endpoint" && showTerminalCommandPanel && (
        <TerminalCommandPanel bank={bank} serverId={serverId} />
      )}
      {terminalMode === "endpoint" ? (
        <div className="terminal__drawer__tableContainer">
          <EndpointMessagesProvider>
            <TerminalEndpointTable />
          </EndpointMessagesProvider>
        </div>
      ) : (
        showTerminalTable && (
          <div className="terminal__drawer__tableContainer">
            <TerminalTable
              liftId={liftId}
              bankId={bankId}
              socketMessages={socketMessages}
              setSocketMessages={setSocketMessages}
              filterType={filterType}
              liftNameMap={liftNameMap}
              capturedMessagesRef={capturedMessagesRef}
            />
          </div>
        )
      )}
      {showTerminalJsonViewer && (
        <div className="generalStyles__flex" style={{ marginTop: "100px" }}>
          <div style={{ marginRight: "20px", width: "100%" }}>
            <div
              className={`monitoring__card__${theme}`}
              style={{
                height: "auto",
                marginTop: "20px",
                paddingTop: "15px",
                marginRight: "15px",
                width: "100%",
              }}
            >
              {bank && bank.id && (
                <div className="monitoring__card__header">
                  <div className="generalStyles__flex">
                    <MonitoringHeartBeat bankId={String(bank.id)} type="TOOLTIP" />
                  </div>
                </div>
              )}
              {bank && bank.id && bank.type === LiftType.ELEVATOR && (
                <>
                  <MonitoringElevatorsFullView
                    bank={bank}
                    serverId={serverId}
                    channelId={channelId}
                    enableActions={false}
                  />
                  <TerminalBankCommands bank={bank} />
                </>
              )}
              {bank && bank.id && bank.type === LiftType.ESCALATOR && (
                <MonitoringEscalatorsFullView
                  bank={bank}
                  serverId={serverId}
                  channelId={channelId}
                  enableActions
                />
              )}
              {bank && bank.id && bank.type === LiftType.MOVINGWALK && (
                <MonitoringMovingWalkFullView
                  bank={bank}
                  serverId={serverId}
                  channelId={channelId}
                />
              )}
            </div>
          </div>
          <div className="generalStyles__width100">
            <TerminalJsonViewer socketMessages={socketMessages} />
          </div>
        </div>
      )}
      {terminalShowBankFloors && <TerminalBankFloors bank={bank} />}
    </div>
  );
}

export default TerminalPage;
